/*
 * This uses intersection observers to detect when an element becomes sticky.
 * It's most useful for detecting when a `position: sticky` element becomes stuck.
 */

import each from 'lodash/each'
import throttle from 'lodash/throttle'

const stickyElementSelector = '[data-sticky]'
const stickyClassname = 'is-stuck'

let observers = []
let disconnectorCallbacks = []

function getBlockLevelAncestor(el) {
  let parent = el.parentElement
  while (parent) {
    const style = window.getComputedStyle(parent)
    if (/(block|flex|grid|table|inline-block)/.test(style.display)) {
      return parent
    }
    parent = parent.parentElement
  }
  return document.documentElement // Default to document if none found
}

function getScrollableAncestor(el) {
  let parent = el.parentElement
  while (parent && parent !== document.body) {
    const style = window.getComputedStyle(parent)
    if (/(auto|scroll)/.test(style.overflowY)) {
      return parent
    }
    parent = parent.parentElement
  }
  return window
}

function setupStickyObserver(el) {
  if (el.hasAttribute('data-sticky-initialized')) {
    return
  }

  const scrollableAncestor = getScrollableAncestor(el)
  const blockAncestor = getBlockLevelAncestor(el)
  let stickyElementStyle = null
  let stickyElementTop = 0

  const determineStickyState = () => {
    if (!stickyElementStyle) {
      stickyElementStyle = window.getComputedStyle(el)
      const stickyOffset = parseInt(stickyElementStyle.getPropertyValue('--sticky-offset'), 10)
      stickyElementTop = !Number.isNaN(stickyOffset) ? stickyOffset : parseInt(stickyElementStyle.top, 10)
    }
    const boundingClientRect = el.getBoundingClientRect()
    const currentTop = boundingClientRect.top
    const { scrollY } = window
    const blockAncestorBottom = blockAncestor.getBoundingClientRect().bottom + scrollY
    const isPastBlockAncestorBottom = scrollY + stickyElementTop + el.offsetHeight >= blockAncestorBottom

    el.classList.toggle(stickyClassname, currentTop <= stickyElementTop && !isPastBlockAncestorBottom)
  }

  el.setAttribute('data-sticky-initialized', '1')

  const throttled = throttle(determineStickyState, 200)
  scrollableAncestor.addEventListener('scroll', throttled)
  disconnectorCallbacks.push(() => {
    scrollableAncestor.removeEventListener('scroll', throttled)
  })
}

function setupStickyObservers(container) {
  if (!container) {
    return
  }

  // Initialize existing elements:

  const existingEls = container.querySelectorAll(stickyElementSelector)
  if (existingEls.length) {
    each(existingEls, (el) => {
      setupStickyObserver(el)
    })
  }

  // Observe new elements:

  const observer = new MutationObserver((mutations) => {
    each(mutations, (mutation) => {
      each(mutation.addedNodes, (node) => {
        if (node.nodeType !== 1) {
          return
        }

        const els = node.querySelectorAll(stickyElementSelector)
        if (!els.length) {
          return
        }

        each(els, (el) => {
          setupStickyObserver(el)
        })
      })
    })
  })

  observer.observe(container, {
    childList: true,
    subtree: true,
    attributes: false,
    characterData: false,
  })
}

function destroyStickyObservers() {
  observers.forEach((observer) => observer.disconnect())
  observers = []

  disconnectorCallbacks.forEach((cb) => {
    cb()
  })
  disconnectorCallbacks = []
}

export { setupStickyObservers, destroyStickyObservers }
